import type { MeResponse } from '@dbrain/shared';
import { OppyWordmark, Spinner } from '@dbrain/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { api, ApiClientError, isApiError } from '../api/client.js';
import { keys } from '../api/hooks.js';
import { useMeta } from '../experience.js';
import { setLocale } from '../i18n/index.js';
import { PersonalLoginPage } from './bireysel/PersonalAuth.js';

/**
 * Sign-in of the kurumsal edition (Phase 13d; GirisKurumsal.dc.html,
 * GirisKurumsalHata.dc.html): the black half with the Oppy logo, the form
 * on the right. "Email" takes the company address or the directory user
 * name, as typed; the password goes to the company's authentication
 * service through the API and nowhere else. There is no sign-up here.
 */
export function SignInPage() {
  const meta = useMeta();
  const { t } = useTranslation();
  if (meta.isPending) {
    return (
      <div className="page-center">
        <Spinner label={t('common.loading')} />
      </div>
    );
  }
  return meta.data?.capabilities?.directoryLogin ? <DirectoryLoginPage /> : <PersonalLoginPage />;
}

export function SignInLayout({ children, footer }: { children: ReactNode; footer: string }) {
  return (
    <div className="signin" data-testid="signin-page">
      <div className="signin__brand">
        <OppyWordmark className="signin__wordmark" />
        {/* D-184: the company emblem in white, as the iPhone app's sign-in (GirisKurumsal.dc.html) */}
        <img className="signin__emblem" src="/brand/emblem-white.png" alt="" aria-hidden="true" data-testid="signin-emblem" />
      </div>
      <main id="main" className="signin__main">
        <div className="signin__panel">{children}</div>
        <p className="signin__footer">
          <LockIcon />
          <span>{footer}</span>
        </p>
      </main>
    </div>
  );
}

export function useSignedIn() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  return async (me: MeResponse) => {
    queryClient.setQueryData(keys.me, me);
    await setLocale(me.user.locale);
    const from = (location.state as { from?: string } | null)?.from;
    void navigate(from ?? '/', { replace: true });
  };
}

/**
 * The banner of a failed sign-in: a strong first sentence, then what to do. `field`: the credentials were
 * refused. Only a server that does not answer (no response, 5xx) "can't be reached"; a refused request (4xx)
 * never is.
 */
export function failureOf(error: unknown): { key: 'invalid' | 'unavailable' | 'locked' | 'deactivated' | 'relink' | 'check' | 'failed'; field: boolean } {
  if (isApiError(error, 'invalid_credentials')) return { key: 'invalid', field: true };
  if (isApiError(error, 'rate_limited')) return { key: 'locked', field: false };
  if (isApiError(error, 'account_deactivated')) return { key: 'deactivated', field: false };
  if (isApiError(error, 'account_relink_required')) return { key: 'relink', field: false };
  if (isApiError(error, 'validation_failed')) return { key: 'check', field: false };
  const status = error instanceof ApiClientError ? error.status : 0;
  return { key: status === 0 || status >= 500 ? 'unavailable' : 'failed', field: false };
}

/** The server refused the authentication code's form (not 6 digits): the code field's error, not a banner. */
const codeRefused = (error: unknown) => isApiError(error, 'validation_failed') && (error.fields ?? []).some((f) => f.path === 'totp');

/**
 * D-114: the seconds still to wait after too many failed attempts (the answer's Retry-After), counting
 * down; 0 when there is nothing to wait for.
 */
export function useWait(error: unknown): number {
  const until = useMemo(() => (isApiError(error, 'rate_limited') && error.retryAfter ? Date.now() + error.retryAfter * 1000 : 0), [error]);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!until) return;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [until]);
  return until ? Math.max(0, Math.ceil((until - now) / 1000)) : 0;
}

export const clock = (seconds: number) => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

function Failure({ error, prefix, wait }: { error: unknown; prefix: 'auth.directory' | 'auth.breakGlass'; wait: number }) {
  const { t } = useTranslation();
  const { key } = failureOf(error);
  const timed = key === 'locked' && isApiError(error, 'rate_limited') && error.retryAfter !== undefined;
  const action = !timed
    ? t(`${prefix}.errors.${key}.action`)
    : wait <= 0
      ? t('auth.directory.waitOver')
      : wait < 60
        ? t('auth.directory.wait_seconds', { count: wait })
        : t('auth.directory.wait_minutes', { count: Math.ceil(wait / 60) });
  return (
    <div className="signin__alert" role="alert" data-testid="signin-error">
      <AlertIcon />
      <span>
        <strong>{t(`${prefix}.errors.${key}.title`)}</strong> {action}
      </span>
    </div>
  );
}

function PasswordInput({ value, onChange, invalid, inputRef, testId }: { value: string; onChange: (v: string) => void; invalid: boolean; inputRef: React.Ref<HTMLInputElement>; testId: string }) {
  const { t } = useTranslation();
  const id = useId();
  const [shown, setShown] = useState(false);
  return (
    <div className="signin__field">
      <label htmlFor={id}>{t('auth.directory.password')}</label>
      <div className="signin__password">
        <input
          ref={inputRef}
          id={id}
          className="signin__input"
          type={shown ? 'text' : 'password'}
          autoComplete="current-password"
          required
          value={value}
          aria-invalid={invalid || undefined}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
        />
        <button
          type="button"
          className="signin__reveal"
          aria-label={shown ? t('auth.directory.hidePassword') : t('auth.directory.showPassword')}
          aria-pressed={shown}
          onClick={() => setShown((v) => !v)}
          data-testid="signin-reveal"
        >
          <EyeIcon off={shown} />
        </button>
      </div>
    </div>
  );
}

export function DirectoryLoginPage() {
  const { t } = useTranslation();
  const signedIn = useSignedIn();
  const usernameId = useId();
  const passwordRef = useRef<HTMLInputElement>(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const login = useMutation({
    mutationFn: () => api<MeResponse>('POST', '/api/auth/directory/login', { username, password, remember }),
    onSuccess: signedIn,
    onError: (error) => {
      // a refused password is typed again
      if (failureOf(error).field) {
        setPassword('');
        passwordRef.current?.focus();
      }
    },
  });
  const failure = login.error ? failureOf(login.error) : null;
  const wait = useWait(login.error);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !password || wait > 0) return;
    login.mutate();
  };
  return (
    <SignInLayout footer={t('auth.directory.footer')}>
      <header className="signin__head">
        <h1>{t('auth.directory.title')}</h1>
        <p>{t('auth.directory.subtitle')}</p>
      </header>
      <form className="signin__form" onSubmit={submit} noValidate>
        {login.error ? <Failure error={login.error} prefix="auth.directory" wait={wait} /> : null}
        <div className="signin__field">
          <label htmlFor={usernameId}>{t('auth.directory.email')}</label>
          <input
            id={usernameId}
            className="signin__input"
            type="text"
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            required
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            data-testid="signin-username"
          />
        </div>
        <PasswordInput value={password} onChange={setPassword} invalid={failure?.field === true} inputRef={passwordRef} testId="signin-password" />
        <div className="signin__row">
          <label className="signin__check">
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} data-testid="signin-remember" />
            {t('auth.directory.remember')}
          </label>
          <button type="button" className="signin__link" aria-expanded={forgotOpen || failure?.field === true} onClick={() => setForgotOpen((v) => !v)} data-testid="signin-forgot">
            {t('auth.directory.forgot')}
          </button>
        </div>
        {forgotOpen || failure?.field ? (
          <div className="signin__note" role="note" data-testid="signin-forgot-note">
            <InfoIcon />
            <span>{t('auth.directory.forgotNote')}</span>
          </div>
        ) : null}
        <button type="submit" className="signin__submit" disabled={login.isPending || wait > 0} aria-busy={login.isPending || undefined} data-testid="signin-submit">
          {wait > 0 ? t('auth.directory.countdown', { time: clock(wait) }) : t('auth.directory.submit')}
        </button>
      </form>
    </SignInLayout>
  );
}

/**
 * The break-glass admin (06 §22.6): a local owner who signs in with a
 * password and a TOTP code when the company sign-in cannot be used. Not
 * linked from anywhere; every sign-in is in the audit log.
 */
export function BreakGlassLoginPage() {
  const { t } = useTranslation();
  const signedIn = useSignedIn();
  const emailId = useId();
  const codeId = useId();
  const passwordRef = useRef<HTMLInputElement>(null);
  const codeRef = useRef<HTMLInputElement>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  // the code is checked here first: six digits (spaces allowed), as the server checks it
  const [codeError, setCodeError] = useState(false);
  const login = useMutation({
    mutationFn: () => api<MeResponse>('POST', '/api/auth/login', { email, password, totp: totp.replace(/\s+/g, '') }),
    onSuccess: signedIn,
    onError: (error) => {
      if (failureOf(error).field) {
        setPassword('');
        setTotp('');
        passwordRef.current?.focus();
      } else if (codeRefused(error)) codeRef.current?.focus();
    },
  });
  const failure = login.error ? failureOf(login.error) : null;
  const codeInvalid = codeError || (login.error !== null && codeRefused(login.error));
  const wait = useWait(login.error);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password || wait > 0) return;
    if (!/^\d{6}$/.test(totp.replace(/\s+/g, ''))) {
      setCodeError(true);
      codeRef.current?.focus();
      return;
    }
    login.mutate();
  };
  return (
    <SignInLayout footer={t('auth.breakGlass.footer')}>
      <header className="signin__head">
        <h1>{t('auth.breakGlass.title')}</h1>
        <p>{t('auth.breakGlass.subtitle')}</p>
      </header>
      <form className="signin__form" onSubmit={submit} noValidate>
        {login.error && !codeRefused(login.error) ? <Failure error={login.error} prefix="auth.breakGlass" wait={wait} /> : null}
        <div className="signin__field">
          <label htmlFor={emailId}>{t('auth.directory.email')}</label>
          <input id={emailId} className="signin__input" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="breakglass-email" />
        </div>
        <PasswordInput value={password} onChange={setPassword} invalid={failure?.field === true} inputRef={passwordRef} testId="breakglass-password" />
        <div className="signin__field">
          <label htmlFor={codeId}>{t('auth.breakGlass.code')}</label>
          <input
            ref={codeRef}
            id={codeId}
            className="signin__input"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={7}
            required
            value={totp}
            aria-invalid={failure?.field || codeInvalid || undefined}
            aria-describedby={`${codeId}-hint`}
            onChange={(e) => {
              setTotp(e.target.value);
              if (codeInvalid) {
                setCodeError(false);
                login.reset();
              }
            }}
            data-testid="breakglass-code"
          />
          {codeInvalid ? (
            <span id={`${codeId}-hint`} className="signin__hint signin__hint--error" role="alert" data-testid="breakglass-code-error">
              {t('auth.breakGlass.codeInvalid')}
            </span>
          ) : (
            <span id={`${codeId}-hint`} className="signin__hint">
              {t('auth.breakGlass.codeHint')}
            </span>
          )}
        </div>
        <button type="submit" className="signin__submit" disabled={login.isPending || wait > 0} aria-busy={login.isPending || undefined} data-testid="breakglass-submit">
          {wait > 0 ? t('auth.directory.countdown', { time: clock(wait) }) : t('auth.directory.submit')}
        </button>
      </form>
    </SignInLayout>
  );
}

function LockIcon() {
  return (
    <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  );
}

export function AlertIcon() {
  return (
    <svg className="signin__alert-icon" aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v4M12 16h.01" />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg className="signin__note-icon" aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5M12 8h.01" />
    </svg>
  );
}

export function EyeIcon({ off }: { off: boolean }) {
  return (
    <svg aria-hidden="true" width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
      <circle cx="12" cy="12" r="3" />
      {off ? <path d="M3 3l18 18" /> : null}
    </svg>
  );
}
